import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from 'react';
import { useSocialProviders } from '../hooks/auth-actions.js';
import { useAuthClient } from '../hooks/auth-provider.js';
import { mergeFormClasses } from './shared.js';
import { writePendingSocialLogin } from './social-login-state.js';
export { SOCIAL_LOGIN_PENDING_KEY } from './social-login-state.js';
/**
 * Renders one button per social provider the API advertises. Clicking a button:
 *   1) Asks the API to start a social login (`POST /auth/social/<id>/start` analogue),
 *      which returns `{redirectUrl, initCode}`.
 *   2) Parks `{initCode, next}` in tab-scoped session storage under
 *      `SOCIAL_LOGIN_PENDING_KEY`. The `initCode` is also what the API sent to the IdP as
 *      OAuth `state`, so parking it is what lets the callback prove the sign-in it is being
 *      asked to complete is the one this browser started (see `social-login-state.ts`).
 *   3) Navigates the browser to `redirectUrl` (the IdP's OAuth consent screen).
 *
 * After the IdP round-trips and the callback fires, the user lands on `callbackPath` with
 * `?code=<returnToCode>&state=<initCode>` in the URL. The `<SocialCallback>` page compares
 * the returned `state` with the parked value, calls the exchange endpoint only if they match,
 * and stores the resulting session token. Mount `<SocialCallback>` on the same path you pass
 * as `callbackPath`.
 *
 * Renders nothing while the providers list is still loading or empty, so it's safe to drop
 * into a login page unconditionally.
 */
export function SocialSignInButtons(props) {
    const classes = mergeFormClasses(props.classNames);
    const client = useAuthClient();
    const { providers, loading } = useSocialProviders();
    const [busyProvider, setBusyProvider] = React.useState(null);
    const [error, setError] = React.useState(null);
    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    const callbackPath = props.callbackPath ?? '/auth/callback';
    const callbackUrl = origin.length > 0 ? `${origin}${callbackPath}` : callbackPath;
    // Default `next` = where to land after a successful sign-in. We read the current path,
    // BUT if the user clicked a social button from an auth surface (`/login`, `/signup`,
    // `/recovery`, `/auth/...`), defaulting to that path would just bounce them back to the
    // login page after auth. In that case fall through to '/' (host apps that want a
    // different default should pass `next` explicitly).
    const currentPath = typeof window !== 'undefined' ? window.location.pathname : '/';
    const isAuthPath = /^\/(login|signup|recovery|verification|mfa|auth)\b/.test(currentPath);
    const defaultNext = isAuthPath
        ? '/'
        : `${currentPath}${typeof window !== 'undefined' ? window.location.search : ''}`;
    const next = props.next ?? defaultNext;
    const mode = props.mode ?? 'login';
    const handleClick = React.useCallback(async (providerId) => {
        setError(null);
        setBusyProvider(providerId);
        try {
            const start = mode === 'signup'
                ? await client.startSocialSignup({ provider: providerId, returnTo: callbackUrl })
                : await client.startSocialLogin({ provider: providerId, returnTo: callbackUrl });
            // No initCode means no `state` to park, which means the callback would have nothing to
            // compare the IdP's `state` against. Refuse here rather than redirect into a flow the
            // callback is required to reject — the user gets the failure at the click, not after a
            // round trip through Google.
            if (start.initCode === undefined || start.initCode.length === 0) {
                throw new Error('The server did not issue a sign-in correlator, so this social login cannot be verified. Try again, or use email and password.');
            }
            if (!writePendingSocialLogin({ initCode: start.initCode, next, provider: providerId, mode })) {
                throw new Error('This browser would not let the sign-in be recorded, so it cannot be completed safely. Enable site storage for this site, or use email and password.');
            }
            if (typeof window !== 'undefined') {
                window.location.assign(start.redirectUrl);
            }
        }
        catch (reason) {
            setBusyProvider(null);
            setError(reason instanceof Error ? reason.message : 'Could not start social sign-in.');
        }
    }, [client, callbackUrl, next, mode]);
    if (loading || providers.length === 0) {
        return null;
    }
    /* verbPrefix tweaks the per-button label to match the mode. Falls back to "Continue with"
       for login (a familiar phrase) and "Sign up with" for signup (clearer intent). */
    const verbPrefix = mode === 'signup' ? 'Sign up with' : 'Continue with';
    const heading = mode === 'signup' ? 'Or sign up with' : 'Or continue with';
    return (_jsxs("div", { className: "auth-ui-social", children: [props.showHeading === false ? null : _jsx("p", { className: classes.hint, children: heading }), _jsx("div", { className: "auth-ui-social-list", children: providers.map((provider) => {
                    const onClick = () => {
                        void handleClick(provider.id);
                    };
                    const disabled = busyProvider !== null;
                    if (props.renderProvider !== undefined) {
                        return (_jsx(React.Fragment, { children: props.renderProvider({
                                id: provider.id,
                                displayName: provider.displayName,
                                onClick,
                                disabled,
                                busy: busyProvider === provider.id,
                            }) }, provider.id));
                    }
                    return (_jsx("button", { type: "button", onClick: onClick, className: classes.secondaryButton, disabled: disabled, children: busyProvider === provider.id
                            ? `Redirecting…`
                            : `${verbPrefix} ${provider.displayName}` }, provider.id));
                }) }), error !== null ? (_jsx("p", { className: classes.error, role: "alert", children: error })) : null, props.after] }));
}
/**
 * Convenience wrapper that pins `mode='signup'` so signup pages don't have to spell it out.
 * Equivalent to `<SocialSignInButtons mode="signup" {...props} />`. Buttons render as
 * "Sign up with <Provider>" and the post-callback exchange skips the "no silent account
 * creation" guard (since creation is the goal here).
 */
export function SocialSignupButtons(props) {
    return _jsx(SocialSignInButtons, { ...props, mode: "signup" });
}
//# sourceMappingURL=SocialSignInButtons.js.map